import { Button, Css, ModalBody, ModalFooter, ModalHeader, SelectField, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useMemo, useState } from "react";
import { ConfigureBidGroupsStepQuery } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { DimensionTypeCheckboxes } from "src/routes/bids/plan-based-bid-package/components/DimensionTypeCheckboxes";
import {
  buildDimensions,
  PlanBasedBidPackageBidPlanGroup,
  PlanBasedBidPackageFormState,
} from "src/routes/bids/plan-based-bid-package/utils";

type AddOptionModalProps = {
  formState: ObjectState<PlanBasedBidPackageFormState>;
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"];
  potentialBidPlanOptions: ConfigureBidGroupsStepQuery["bidPackage"]["potentialBidPlanOptions"];
  editingGroup?: ObjectState<PlanBasedBidPackageBidPlanGroup>;
};

export function AddOptionModal(props: AddOptionModalProps) {
  const { formState, dimensionResults, potentialBidPlanOptions, editingGroup } = props;
  const { closeModal } = useModal();
  const isEdit = !!editingGroup;
  const [globalOptionId, setGlobalOptionId] = useState<string | undefined>(
    editingGroup?.globalOptionId.value ?? undefined,
  );
  // Seed the dimension type checkboxes from the edited option's current dimensions; start empty when creating.
  const [selectedTypeIds, setSelectedTypeIds] = useState<string[]>(
    isEdit
      ? (editingGroup?.dimensions.value ?? []).map((d) => d.globalOptionTypeId).filter((id): id is string => !!id)
      : [],
  );
  // Filter out options already used as a BidPlanGroup in this bid, the option being edited stays available.
  const availableOptions = useMemo(() => {
    const usedGlobalOptionIds = (formState.bidPlanGroups.value ?? [])
      .map((g) => g.globalOptionId)
      .filter((id) => !!id && id !== editingGroup?.globalOptionId.value);
    return potentialBidPlanOptions
      .map((o) => o.globalOption)
      .filter((go) => !usedGlobalOptionIds.includes(go.id))
      .sortByKey("name");
  }, [formState.bidPlanGroups, potentialBidPlanOptions, editingGroup]);

  function save() {
    if (!globalOptionId) return;
    if (isEdit && editingGroup) {
      editingGroup.dimensions.set(
        buildDimensions(selectedTypeIds, editingGroup.dimensions.value ?? [], dimensionResults),
      );
    } else {
      const groups = formState.bidPlanGroups.value ?? [];
      const maxOrder = groups.reduce((m, g) => Math.max(m, g.order ?? 0), -1);
      formState.bidPlanGroups.set([
        ...groups,
        { globalOptionId, order: maxOrder + 1, dimensions: buildDimensions(selectedTypeIds, [], dimensionResults) },
      ]);
    }
    closeModal();
  }

  return (
    <>
      <ModalHeader>{isEdit ? "Edit Option" : "Add an Option"}</ModalHeader>
      <ModalBody>
        <div css={Css.mb3.$}>Define the option and attributes to include as lines in your bid sheet.</div>
        <div css={Css.df.fdc.gap3.$}>
          <SelectField
            label="Option"
            options={availableOptions}
            value={globalOptionId}
            onSelect={(id) => setGlobalOptionId(id)}
            getOptionValue={(o) => o.id}
            getOptionLabel={(o) => o.name}
            readOnly={isEdit}
          />
          <DimensionTypeCheckboxes
            dimensionResults={dimensionResults}
            selectedTypeIds={selectedTypeIds}
            onChange={setSelectedTypeIds}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="tertiary" onClick={closeModal} />
        <Button label="Save" onClick={save} disabled={!globalOptionId} />
      </ModalFooter>
    </>
  );
}
